import { type Patient, type TriageFindings, explainCategory } from '@orgsim/shared';
import { useEffect, useRef } from 'react';
import { CATEGORY_COLORS } from '../../signs/svg';
import { CATEGORY_LABELS } from '../labels';
import { TriageFields } from './TriageFields';

type Props = {
  patient: Patient;
  limitedResources: boolean;
  open: boolean;
  onOpen: () => void;
  onChange: (p: Patient) => void;
  onRemove: () => void;
};

export function PatientForm({
  patient,
  limitedResources,
  open,
  onOpen,
  onChange,
  onRemove,
}: Props) {
  const itemRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) itemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [open]);
  const set = (patch: Partial<Patient>) => onChange({ ...patient, ...patch });
  const setFinding = (i: number, patch: Partial<{ question: string; answer: string }>) =>
    set({ findings: patient.findings.map((f, j) => (j === i ? { ...f, ...patch } : f)) });
  const setTriage = (patch: Partial<TriageFindings>) => {
    const triage = { ...patient.triage, ...patch };
    const { category } = explainCategory(triage, limitedResources);
    set({ triage, expectedCategory: category });
  };
  const outcome = explainCategory(patient.triage, limitedResources);

  return (
    <div ref={itemRef} className={`editor-item ${open ? 'selected' : ''}`}>
      <button type="button" onClick={onOpen} aria-expanded={open} className="editor-item-header">
        <span>
          Patient {patient.number}: {patient.summary || 'ohne Kurztext'}
        </span>
        <span className="text-xs text-neutral-500">
          {CATEGORY_LABELS[patient.expectedCategory]}
        </span>
      </button>
      {open ? (
        <div className="space-y-2 border-t border-neutral-800 p-2">
          <label className="block">
            Nummer
            <input
              type="number"
              min={1}
              value={patient.number}
              onChange={(e) => set({ number: Number(e.target.value) })}
              className="mt-1 w-full rounded bg-neutral-800 px-2 py-1"
            />
          </label>
          <label className="block">
            Kurztext
            <input
              value={patient.summary}
              onChange={(e) => set({ summary: e.target.value })}
              placeholder="m, ca. 40, eingeklemmt"
              className="mt-1 w-full rounded bg-neutral-800 px-2 py-1"
            />
          </label>
          <div>
            <div className="mb-1 text-xs uppercase tracking-wide text-neutral-400">Sichtung</div>
            <TriageFields
              findings={patient.triage}
              limitedResources={limitedResources}
              onChange={setTriage}
            />
          </div>
          <div className="flex items-center gap-2 rounded bg-neutral-900 px-2 py-2">
            <span
              className="inline-block h-4 w-4 rounded-full"
              style={{ background: CATEGORY_COLORS[outcome.category] }}
            />
            <div>
              <div className="font-medium">{CATEGORY_LABELS[outcome.category]}</div>
              <div className="text-xs text-neutral-400">
                {outcome.reason}, deshalb {outcome.category}
              </div>
            </div>
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <span>Weitere Befunde, die der Algorithmus nicht abfragt</span>
              <button
                type="button"
                onClick={() =>
                  set({ findings: [...patient.findings, { question: '', answer: '' }] })
                }
                className="text-xs text-red-400"
              >
                + Befund
              </button>
            </div>
            {patient.findings.map((f, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: findings have no id, order is the identity
              <div key={i} className="mb-1 flex gap-1">
                <input
                  value={f.question}
                  onChange={(e) => setFinding(i, { question: e.target.value })}
                  placeholder="Frage"
                  className="w-1/2 rounded bg-neutral-800 px-2 py-1"
                />
                <input
                  value={f.answer}
                  onChange={(e) => setFinding(i, { answer: e.target.value })}
                  placeholder="Antwort"
                  className="w-1/2 rounded bg-neutral-800 px-2 py-1"
                />
                <button
                  type="button"
                  aria-label="Befund entfernen"
                  onClick={() => set({ findings: patient.findings.filter((_, j) => j !== i) })}
                  className="px-1 text-neutral-500"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <label className="block">
            Hinweis für den Ausbilder
            <textarea
              value={patient.instructorNote ?? ''}
              onChange={(e) => set({ instructorNote: e.target.value || undefined })}
              rows={2}
              className="mt-1 w-full rounded bg-neutral-800 px-2 py-1"
            />
          </label>
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-neutral-400 hover:text-red-300"
          >
            Patient entfernen
          </button>
        </div>
      ) : null}
    </div>
  );
}
